import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Chip, Typography} from '@mui/material';

import {
  gapsAndSignalsDotSx,
  gapsAndSignalsListRowSx,
  gapsAndSignalsNotMetChipSx,
  gapsAndSignalsPaperSx,
  gapsAndSignalsPartialGapChipSx,
  gapsAndSignalsRedeploymentChipSx,
  gapsAndSignalsStrengthRowSx,
} from './workforceProfileGapsAndSignalsSectionSx';
import {WorkforceProfileRecommendationCallout} from './WorkforceProfileRecommendationCallout';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import type {CompanyWorkforceProfileDetail} from '../../types/WorkforceProfileTypes';
import {getCompetencyRowName} from '../../utils/WorkforceProfileDetail/getCompetencyRowName';

export interface Props {
  profile: CompanyWorkforceProfileDetail;
  overall: number;
  totalGapCount: number;
  notMetCoreCompetencies: NonNullable<CompanyWorkforceProfileDetail['coreCompetencyRows']>;
  notMetHardSkills: NonNullable<CompanyWorkforceProfileDetail['hardSkillRows']>;
  notMetTools: NonNullable<CompanyWorkforceProfileDetail['toolRows']>;
  notMetDomainKnowledge: NonNullable<CompanyWorkforceProfileDetail['domainKnowledgeRows']>;
  notMetSoftSkills: NonNullable<CompanyWorkforceProfileDetail['softSkillRows']>;
  notMetResponsibilities: NonNullable<CompanyWorkforceProfileDetail['responsibilitiesRows']>;
}

export const WorkforceProfileGapsAndSignalsSection = ({
  profile,
  overall,
  totalGapCount,
  notMetCoreCompetencies,
  notMetHardSkills,
  notMetTools,
  notMetDomainKnowledge,
  notMetSoftSkills,
  notMetResponsibilities,
}: Props) => {
  const currentLanguage = useContext(LanguageContext);
  const {t} = useTranslation('translation', {keyPrefix: 'workforceProfilesPage.detail'});

  return (
    <Box>
      <WorkforceProfileRecommendationCallout overall={overall} gapCount={totalGapCount} />

      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2, mt: 2}}>
        <EmpionPaper sx={gapsAndSignalsPaperSx}>
          <Typography variant='subtitle2' sx={{fontWeight: 600, mb: 1.5}}>
            {t('identifiedGaps')}
          </Typography>
          {profile.highlights?.gaps.map((gap) => (
            <Box key={gap} sx={gapsAndSignalsListRowSx}>
              <Box sx={gapsAndSignalsDotSx('partial')} />
              <Typography variant='body2' sx={{flex: 1}}>
                {gap}
              </Typography>
              <Chip label={t('gapBadge')} size='small' sx={gapsAndSignalsPartialGapChipSx} />
            </Box>
          ))}
          {notMetCoreCompetencies.map((row) => (
            <Box key={`core-${row.name}`} sx={gapsAndSignalsListRowSx}>
              <Box sx={gapsAndSignalsDotSx('notMet')} />
              <Typography variant='body2' sx={{flex: 1}}>
                {getCompetencyRowName(row, currentLanguage)}
              </Typography>
              <Chip label={t('notMet')} size='small' sx={gapsAndSignalsNotMetChipSx} />
            </Box>
          ))}
          {notMetHardSkills.map((row) => (
            <Box key={`hard-${row.name}`} sx={gapsAndSignalsListRowSx}>
              <Box sx={gapsAndSignalsDotSx('notMet')} />
              <Typography variant='body2' sx={{flex: 1}}>
                {getCompetencyRowName(row, currentLanguage)}
              </Typography>
              <Chip label={t('notMet')} size='small' sx={gapsAndSignalsNotMetChipSx} />
            </Box>
          ))}
          {notMetTools.map((row) => (
            <Box key={`tools-${row.name}`} sx={gapsAndSignalsListRowSx}>
              <Box sx={gapsAndSignalsDotSx('notMet')} />
              <Typography variant='body2' sx={{flex: 1}}>
                {getCompetencyRowName(row, currentLanguage)}
              </Typography>
              <Chip label={t('notMet')} size='small' sx={gapsAndSignalsNotMetChipSx} />
            </Box>
          ))}
          {notMetDomainKnowledge.map((row) => (
            <Box key={`domain-${row.name}`} sx={gapsAndSignalsListRowSx}>
              <Box sx={gapsAndSignalsDotSx('notMet')} />
              <Typography variant='body2' sx={{flex: 1}}>
                {getCompetencyRowName(row, currentLanguage)}
              </Typography>
              <Chip label={t('notMet')} size='small' sx={gapsAndSignalsNotMetChipSx} />
            </Box>
          ))}
          {notMetSoftSkills.map((row) => (
            <Box key={`soft-${row.name}`} sx={gapsAndSignalsListRowSx}>
              <Box sx={gapsAndSignalsDotSx('notMet')} />
              <Typography variant='body2' sx={{flex: 1}}>
                {getCompetencyRowName(row, currentLanguage)}
              </Typography>
              <Chip label={t('notMet')} size='small' sx={gapsAndSignalsNotMetChipSx} />
            </Box>
          ))}
          {notMetResponsibilities.map((row) => (
            <Box key={`resp-${row.name}`} sx={gapsAndSignalsListRowSx}>
              <Box sx={gapsAndSignalsDotSx('notMet')} />
              <Typography variant='body2' sx={{flex: 1}}>
                {getCompetencyRowName(row, currentLanguage)}
              </Typography>
              <Chip label={t('notMet')} size='small' sx={gapsAndSignalsNotMetChipSx} />
            </Box>
          ))}
        </EmpionPaper>

        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
          {profile.highlights && profile.highlights.strengths.length > 0 && (
            <EmpionPaper sx={gapsAndSignalsPaperSx}>
              <Typography variant='subtitle2' sx={{fontWeight: 600, mb: 1.5}}>
                {t('potentialStrengths')}
              </Typography>
              {profile.highlights.strengths.map((strength) => (
                <Box key={strength} sx={gapsAndSignalsStrengthRowSx}>
                  <Box sx={gapsAndSignalsDotSx('strength')} />
                  <Typography variant='body2'>{strength}</Typography>
                </Box>
              ))}
            </EmpionPaper>
          )}

          <EmpionPaper sx={gapsAndSignalsPaperSx}>
            <Typography variant='subtitle2' sx={{fontWeight: 600, mb: 1}}>
              {t('redeploymentSignalsLabel')}
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{mb: 1.5, lineHeight: 1.6}}>
              {t('redeploymentHint')}
            </Typography>
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.75}}>
              {overall >= 80 ? (
                <Chip label={t('currentRoleFit')} size='small' sx={gapsAndSignalsRedeploymentChipSx} />
              ) : (
                profile.highlights?.strengths.map((strength) => (
                  <Chip key={strength} label={strength} size='small' sx={gapsAndSignalsRedeploymentChipSx} />
                ))
              )}
            </Box>
          </EmpionPaper>
        </Box>
      </Box>
    </Box>
  );
};
